import { GetServerSideProps } from 'next';
import React from 'react';
import { useDispatch } from 'react-redux';
import { ContextContainer } from '../../components/AppContext';
import EyeballTimeLogger from '../../components/EyeballTimeLogger';
import Layout from '../../components/Layout';
import { Personalize } from '../../components/Personalize/Personalize';
import { IProduct, IProductAccessories } from '../../interfaces';
import {
  actionDidLoadProductAccessories,
  actionDidViewPage
} from '../../redux/actions';
import {
  getProductAccessories,
  getProductsById
} from '../../services/products';
import { getServerSideTranslations } from '../../services/translations';
import { headerPropsForProduct } from '../../utils/htmlHeader';
import { PageView } from '../../utils/pageView';
import {
  getDefaultBuckle,
  getDefaultDial,
  getDefaultStrap,
  isCustomizable
} from '../../utils/product';
import PageViewLogger from './../../components/PageViewLogger';

interface IPersonalizePage {
  products: IProduct[];
  accessories: IProductAccessories | null;
}

const PersonalizePage = ({ products, accessories }: IPersonalizePage) => {
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(actionDidViewPage(PageView.PRODUCT_PERSONALIZE));
    if (accessories) dispatch(actionDidLoadProductAccessories(accessories));
  }, [products, accessories]);

  if (products.length === 0 || !accessories) return null;

  return (
    <Layout headerProps={headerPropsForProduct(products[0])}>
      <ContextContainer>
        <PageViewLogger pageView={PageView.PRODUCT_PERSONALIZE}>
          <EyeballTimeLogger
            key={products[0].id}
            tracker={{
              view: PageView.PRODUCT_PERSONALIZE,
              id: products[0].id
            }}
          >
            <Personalize />
          </EyeballTimeLogger>
        </PageViewLogger>
      </ContextContainer>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id: string = context.params.id as string;
  const selectedStrap = context.query.strap as string;
  const selectedBuckle = context.query.buckle as string;
  const selectedDial = context.query.dial as string;
  let products: IProduct[] = [],
    accessories: IProductAccessories = {};

  try {
    products = await getProductsById(id);
    accessories = await getProductAccessories(products);
  } catch (e) {
    console.error(e);
  }

  return {
    props: {
      ...(await getServerSideTranslations(context.query?.language as string)),
      products,
      accessories: isCustomizable(accessories)
        ? {
            ...accessories,
            dials: [products[0]],
            selectedStrap: getDefaultStrap(accessories, selectedStrap),
            selectedBuckle: getDefaultBuckle(
              accessories,
              selectedStrap,
              selectedBuckle
            ),
            selectedDial: getDefaultDial(products, selectedDial)
          }
        : null
    }
  };
};

export default PersonalizePage;
